import { useEffect, useState } from 'react'
import Api from '../../data'
import CONFIG from '../../config'
import { EVENT_NAMES } from 'constants/eventNames'
import { trackEvent } from 'utils/analyticsService'
import { ANALYTICS_KEYS } from 'constants/analytics-keys'

const polling = [20, 60, 120]
const IACST_RESULT_REDIRECT_URL = CONFIG.iacstResultRedirectURL
const IACST_LOGIN_URL = CONFIG.iacstLoginUrl

function useResultData({
    loading_percent,
    setLoadingPercent,
    setAnalytics,
    hideTimer,
    queryparam,
    testStatus,
    instructions,
    setMockTestSubmit,
}) {
    const isMock = JSON.parse(sessionStorage.getItem('is_mock'))
    const [timerAddress, setTimerAddress] = useState()
    const [redirectToAnalytics, setRedirectToAnalytics] = useState(false)
    const [resultData, setResultData] = useState('')
    const [errorMsg, setErrorMsg] = useState('')
    const [isError, setIsError] = useState(false)
    const [redirectToSuccess, setRedirectToSuccess] = useState(false)

    // since countDown is binded to the interval, we need to use an external variable to update loading_percent
    let t = 0

    function logEvents(eventName, description, res, extraData) {
        const data = {
            [ANALYTICS_KEYS.USER_TEST_ID]:
                'testStatus' in testStatus
                    ? testStatus?.testStatus?.data?.userTestId
                    : 'directly landed to result page so user test id is not available',
            [ANALYTICS_KEYS.TEST_ID]:
                'testData' in instructions
                    ? instructions.testData.id
                    : 'directly landed from instruction page to result page so test id is not available',
            [ANALYTICS_KEYS.TEST_RESULT]: res.result_status,
            [ANALYTICS_KEYS.DOB]: queryparam.dob,
            [ANALYTICS_KEYS.TEST_NAME]:
                'titleEn' in instructions ? instructions.testData.titleEn : null,
            [ANALYTICS_KEYS.DURATION_OF_TEST]:
                'allowedDuration' in instructions
                    ? instructions.testData.allowedDuration
                    : null,
            [ANALYTICS_KEYS.TOTAL_QUESTIONS]:
                'totalQuestion' in instructions
                    ? instructions.testData.totalQuestion
                    : null,
            [ANALYTICS_KEYS.MAX_MARKS]:
                'maxMarks' in instructions ? instructions.testData.maxMarks : null,
            [ANALYTICS_KEYS.GRACE_PERIOD]: queryparam.grace_period,
            ...extraData,
        }

        trackEvent(eventName, description, data)
    }

    useEffect(() => {
        const timer = setInterval(countDown, 2100)
        setTimerAddress(timer)

        return () => clearInterval(timerAddress)
    }, [])

    function countDown() {
        t += 1
        if (loading_percent >= 100 && !isMock) {
            clearInterval(timerAddress)
        } else {
            setLoadingPercent(Math.min(t, 100))
        }
    }
    function redirectToIacstDashboard() {
        window.location.replace(IACST_RESULT_REDIRECT_URL)
    }
    function getIacstResult(apiCallsCount) {
        if (apiCallsCount > 3) {
            setRedirectToSuccess(true)
            redirectToIacstDashboard()
            return
        }
        Api.getoAuthToken()
            .then((res) => res.data)
            .then((res) => {
                Api.getResultData(
                    res.access_token,
                    queryparam.roll_no !== undefined && queryparam.roll_no !== null
                        ? queryparam.roll_no
                        : JSON.parse(localStorage.getItem('roll_no')),
                )
                    .then((res) => res.data)
                    .then((res) => {
                        logEvents(
                            EVENT_NAMES.TEST_RESULT_WEB,
                            'When user lands on the test result screen',
                            res,
                            { [ANALYTICS_KEYS.SUCCESS]: true },
                        )

                        if (res.result_status === null) {
                            setTimeout(() => {
                                getIacstResult(apiCallsCount + 1)
                            }, polling[apiCallsCount] * 1000)
                        } else {
                            setLoadingPercent(100)
                            redirectToIacstDashboard()
                            setResultData(res)
                        }
                    })
                    .catch((error) => {
                        logEvents(
                            EVENT_NAMES.TEST_RESULT_WEB,
                            'When user lands on the test result screen',
                            res,
                            {
                                [ANALYTICS_KEYS.FAILURE]: true,
                                [ANALYTICS_KEYS.REASON_FOR_FAILURE]: error,
                            },
                        )

                        if (
                            error.response != undefined &&
                            error.response.status >= 400
                        ) {
                            setErrorMsg(
                                error.response.data.detailMessage ||
                                    error.response.data.message,
                            )
                            setIsError(true)
                        } else {
                            setIsError(true)
                        }
                    })
            })
            .catch((error) => {
                console.error('error', error)
                if (error.response != undefined && error.response.status >= 400) {
                    setErrorMsg(
                        error.response.data.detailMessage ||
                            error.response.data.message,
                    )
                    setIsError(true)
                } else {
                    setIsError(true)
                }
            })
    }

    function getAnalytics() {
        const userTestId = testStatus?.testStatus?.data?.userTestId
        setMockTestSubmit(true)

        Api.getAnalyticsData(userTestId)
            .then((res) => {
                if (res && res.data) {
                    setAnalytics(res.data)
                    setLoadingPercent(100)
                    setRedirectToAnalytics(true)
                } else {
                    setIsError(true)
                }
            })
            .catch((error) => {
                if (error.response != undefined && error.response.status >= 400) {
                    setErrorMsg(
                        error.response.data.detailMessage ||
                            error.response.data.message,
                    )
                    setIsError(true)
                } else {
                    setIsError(true)
                }
            })
    }

    function fetchData() {
        hideTimer(true)
        setLoadingPercent(0)
        handleBrowserBackClick()
        if (isMock) {
            getAnalytics()
        } else {
            getIacstResult(0)
        }
    }

    function handleBrowserBackClick() {
        window.addEventListener(
            'popstate',
            (event) => {
                if (event.state) {
                    const backUrl = isMock
                        ? 'https://anthedashboard.aakash.ac.in/'
                        : IACST_LOGIN_URL

                    // IACST_LOGIN_URL is empty on the envs without an IACST
                    // deployment; staying put beats navigating to ''.
                    if (backUrl) window.location.href = backUrl
                }
            },
            false,
        )
        window.history.pushState(
            { name: 'browserBack' },
            'on browser back click',
            window.location.href,
        )
    }

    return {
        redirectToAnalytics,
        resultData,
        errorMsg,
        isError,
        redirectToSuccess,
        fetchData,
    }
}

export default useResultData
